/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, FileSearch } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { DOC_TYPES, labelFor, formatDate, relativeTime } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Review Readiness — a structured retrieval view across all documents:
 * filter by document type, period and client, to assemble an evidence pack
 * quickly during an IRD review.
 *
 * Positioning: this is NOT a legal guarantee against penalties. It helps the
 * firm respond faster with complete, organised records.
 */
export default function FirmAudit() {
    const [submissions, setSubmissions] = useState([]);
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("all");
    const [docType, setDocType] = useState("all");
    const [periodFrom, setPeriodFrom] = useState("");
    const [periodTo, setPeriodTo] = useState("");

    async function load() {
        try {
            const params = {};
            if (clientId !== "all") params.client_id = clientId;
            if (docType !== "all") params.document_type = docType;
            const { data } = await api.get("/documents/", { params });
            const filtered = data.filter((s) => {
                if (!periodFrom && !periodTo) return true;
                const ps = s.period_start ? new Date(s.period_start) : null;
                const pe = s.period_end ? new Date(s.period_end) : null;
                if (periodFrom && pe && new Date(periodFrom) > pe) return false;
                if (periodTo && ps && new Date(periodTo) < ps) return false;
                return true;
            });
            setSubmissions(filtered);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    useEffect(() => { api.get("/clients/").then((r) => setClients(r.data)).catch(() => {}); }, []);
    useEffect(() => { load(); /* eslint-disable-line */ }, [clientId, docType, periodFrom, periodTo]);

    const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

    return (
        <div>
            <PageHeader
                eyebrow="Review Readiness"
                title="Document retrieval &amp; evidence pack"
                description="Pull complete, organised records by client, period and document type. When IRD requests evidence, the trail is already structured."
                testId="audit-header"
            >
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                    <span className="font-medium">A note on positioning. </span>
                    ClientOps helps you maintain complete, structured and retrievable records. It does not prevent IRD penalties or guarantee compliance outcomes.
                </div>
            </PageHeader>

            <div className="co-card co-card-pad grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                    <Label className="text-[11px] text-[#5E636E]">Client</Label>
                    <Select value={clientId} onValueChange={setClientId}>
                        <SelectTrigger className="mt-1" data-testid="audit-client"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All clients</SelectItem>
                            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[11px] text-[#5E636E]">Document type</Label>
                    <Select value={docType} onValueChange={setDocType}>
                        <SelectTrigger className="mt-1" data-testid="audit-doctype"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            {DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-[11px] text-[#5E636E]">Period from</Label>
                    <Input type="date" className="mt-1" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} data-testid="audit-from" />
                </div>
                <div>
                    <Label className="text-[11px] text-[#5E636E]">Period to</Label>
                    <Input type="date" className="mt-1" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} data-testid="audit-to" />
                </div>
            </div>

            <div className="mt-6 co-card co-card-pad flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-[#0F1115] text-white">
                    <ShieldCheck className="h-4 w-4" />
                </div>
                <div className="text-sm">
                    <span className="font-medium">{submissions.length}</span>{" "}
                    matching submissions{" "}
                    <span className="text-[#5E636E]">— ready to bundle into an evidence pack.</span>
                </div>
            </div>

            <div className="mt-6 co-card overflow-hidden">
                <table className="w-full text-sm" data-testid="audit-table">
                    <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#5E636E]">
                        <tr>
                            <th className="text-left p-3">Client</th>
                            <th className="text-left p-3">Doc type</th>
                            <th className="text-left p-3">Period</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-left p-3">Files</th>
                            <th className="text-left p-3">Uploaded</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E4E4E7]">
                        {submissions.length === 0 && (
                            <tr><td colSpan="6" className="p-6 text-[#5E636E]"><FileSearch className="h-4 w-4 inline mr-2" /> No submissions match.</td></tr>
                        )}
                        {submissions.map((s) => (
                            <tr key={s.id} data-testid={`audit-row-${s.id}`}>
                                <td className="p-3">{clientName(s.client_id)}</td>
                                <td className="p-3">{labelFor(DOC_TYPES, s.document_type)}</td>
                                <td className="p-3 text-[#5E636E]">{formatDate(s.period_start)} → {formatDate(s.period_end)}</td>
                                <td className="p-3"><StatusBadge status={s.status} /></td>
                                <td className="p-3 font-mono text-[#5E636E]">{s.files?.length || 0}</td>
                                <td className="p-3 text-[#5E636E]">{relativeTime(s.created_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
